/**
 * ContextualSuggestions — Route-aware doc suggestions shown when no search query is active.
 *
 * Filters docSections by keywords or shows the first 5 as "Popular topics".
 */
import { createElement, useMemo } from 'react';
import type { DocSection } from '../../types';

interface ContextualSuggestionsProps {
  docs: DocSection[];
  currentRoute?: string;
  onSelect: (section: DocSection) => void;
}

/** Flatten nested docs for matching */
function flattenDocs(sections: DocSection[]): DocSection[] {
  const result: DocSection[] = [];
  function walk(items: DocSection[]) {
    for (const item of items) {
      result.push(item);
      if (item.children) walk(item.children);
    }
  }
  walk(sections);
  return result;
}

/** Check if a doc section is relevant to a route by keyword matching */
function isRelevant(doc: DocSection, route: string): boolean {
  if (!route) return false;
  const routeLower = route.toLowerCase();
  const routeSegments = routeLower.split('/').filter(Boolean);

  // Check keywords against route segments
  const keywords = (doc.keywords ?? []).map((k) => k.toLowerCase());
  for (const segment of routeSegments) {
    for (const kw of keywords) {
      if (kw.includes(segment) || segment.includes(kw)) return true;
    }
  }

  // Check title against route
  const titleLower = doc.title.toLowerCase();
  for (const segment of routeSegments) {
    if (segment.length > 2 && titleLower.includes(segment)) return true;
  }

  return false;
}

export function ContextualSuggestions({ docs, currentRoute, onSelect }: ContextualSuggestionsProps) {
  const flatDocs = useMemo(() => flattenDocs(docs), [docs]);

  const { heading, items } = useMemo(() => {
    if (currentRoute) {
      const relevant = flatDocs.filter((d) => isRelevant(d, currentRoute));
      if (relevant.length > 0) {
        return { heading: 'Relevant to this page', items: relevant.slice(0, 5) };
      }
    }
    return { heading: 'Popular topics', items: flatDocs.slice(0, 5) };
  }, [flatDocs, currentRoute]);

  if (items.length === 0) {
    return createElement(
      'div',
      {
        style: {
          padding: '24px 16px',
          textAlign: 'center' as const,
          color: 'var(--sw-text-secondary)',
          fontSize: '14px',
        },
      },
      'No documentation available.',
    );
  }

  return createElement(
    'div',
    { style: { padding: '12px 0' } },
    // Header
    createElement(
      'div',
      {
        style: {
          padding: '0 4px 8px',
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.05em',
          color: 'var(--sw-text-secondary)',
        },
      },
      heading,
    ),
    // Cards
    ...items.map((section) =>
      createElement(
        'button',
        {
          key: section.id,
          type: 'button',
          onClick: () => onSelect(section),
          'aria-label': `View documentation: ${section.title}`,
          style: {
            display: 'block',
            width: '100%',
            textAlign: 'left' as const,
            padding: '12px',
            marginBottom: '6px',
            backgroundColor: 'var(--sw-bg-surface)',
            border: '1px solid var(--sw-border)',
            borderRadius: 'var(--sw-border-radius)',
            cursor: 'pointer',
            color: 'var(--sw-text-primary)',
            fontFamily: 'var(--sw-font-family)',
            fontSize: '14px',
            lineHeight: 1.4,
            minHeight: '48px',
            boxSizing: 'border-box' as const,
          },
        },
        createElement(
          'div',
          { style: { fontWeight: 600, marginBottom: '4px' } },
          section.title,
        ),
        createElement(
          'div',
          {
            style: {
              fontSize: '13px',
              color: 'var(--sw-text-secondary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical' as const,
            },
          },
          section.content.slice(0, 100) + (section.content.length > 100 ? '...' : ''),
        ),
      ),
    ),
  );
}
